// Shapes for the route-pending page templates (see page-template-pending.tsx).
// Deliberately plain: static blocks that approximate each page family's
// geometry until the route chunk (and its real skeleton) takes over.

@import './variables.module.less';

.page {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.content {
  display: flex;
  flex-direction: column;
  padding: 0 20px 32px;
  width: 100%;
}

.block {
  border-radius: 12px;
  background: var(--color-surface-secondary, #f3f4f6);
  width: 100%;
}

// Richer templates need shells (field groups, tab bar, feed cards); outlined
// containers keep the gray skeleton blocks inside visible in both themes.
.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.tabBarStub {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 36px;
  margin-top: 16px;
}

.tabSegments {
  flex: 1;
  height: 100%;
  padding: 6px;
  display: flex;
  gap: 6px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 16px;
}

.socialCardStub {
  display: flex;
  flex-direction: column;
  padding: 16px;
  margin-top: 12px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 16px;
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.communityHeader {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 32px 20px 0;
}

.communityTitle {
  font-family: @font-display;
  font-weight: @font-medium;
  font-size: @text-3xl;
  line-height: @leading-tight;
  letter-spacing: -0.5px;
  color: var(--color-text-primary, #111827);
  margin: 0;
}

.communitySubtitle {
  font-size: @text-sm;
  line-height: @leading-5;
  color: var(--color-text-secondary, #6b7280);
  margin: 0;
}

// ---------- Entity detail templates (class / instructor / studio) ----------
// The real detail pages open on a full-bleed identity hero (no back header),
// then cards, above a sticky BottomAction CTA. Geometry mirrors
// class/instructor/studio-detail-page + bottom-action.

// Content column for the detail trio: same side padding as .content, but the
// bottom padding clears the fixed CTA stub the way the real pages reserve
// ~140px for their Book Now bars.
.detailContent {
  display: flex;
  flex-direction: column;
  padding: 18px 20px 140px;
  width: 100%;
}

// Card shell (studio row, instructor card, class/location cards): outlined
// container keeps the gray skeleton blocks visible in both themes (same
// pattern as .socialCardStub; radius 16 matches the real detail cards).
.cardStub {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  margin-top: 12px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 16px;
}

// Two text lines beside an avatar/media stub (name + rating).
.cardLines {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

// Centered identity hero (instructor avatar / studio logo + name + rating).
.centeredHero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 20px 0;
}

// Genre chip pills under the instructor identity.
.chipRow {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}

// Two equal action buttons under the studio identity.
.buttonRow {
  display: flex;
  gap: 12px;
}

// Section label + card group (e.g. "Getting there").
.sectionStub {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
}

// Content column for pages whose submit sits in the fixed .ctaBar — same side
// padding as .content, with the bottom padding clearing the fixed bar the way
// the real BottomAction pages reserve ~140px.
.contentCta {
  display: flex;
  flex-direction: column;
  padding: 0 20px 140px;
  width: 100%;
}

// Centered status view (purchase status / HitPay return): icon, headline,
// note and action button stacked on center, like the real .center views.
.centeredStatus {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px 32px;
  width: 100%;
}

// 2-column variant card grid (market "choose your pass").
.variantGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}

// Sticky bottom CTA — copies BottomAction's fixed slab geometry (700px
// centered, safe-area padding, surface background, top shadow) with a 50px
// radius-14 button stub inside, so the pending shape ends exactly where the
// real Book Now bar will land.
.ctaBar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 700px;
  padding: 14px 20px max(14px, var(--safe-area-inset-bottom, 0px));
  background: var(--color-surface, #ffffff);
  box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.06);
  z-index: 10;
}
